import Progress from 'antd/es/progress';
import React, { useEffect, useRef, useState } from 'react';
import { styled, useTheme } from 'styled-components';

interface AnimatedProgressProps {
  isRunning: boolean;
  isComplete: boolean;
  actualProgress?: number; // 0-1 decimal value
  size?: number;
}

const ProgressContainer = styled.div`
  text-align: center;

  .ant-progress-circle-path {
    stroke: ${({ theme }) => theme.colorPrimary};
  }

  .ant-progress-success-bg {
    stroke: ${({ theme }) => theme.colorPrimary};
  }

  .ant-progress-text {
    color: ${({ theme }) => theme.colorPrimary} !important;
  }
`;

export const AnimatedProgress: React.FC<AnimatedProgressProps> = ({
  isRunning,
  isComplete,
  actualProgress,
  size = 48,
}) => {
  const theme = useTheme();
  const [animatedPercent, setAnimatedPercent] = useState(0);
  const animationRef = useRef<number>();
  const currentPercentRef = useRef<number>(0);
  const animationStartTimeRef = useRef<number>(0);
  const lastTargetRef = useRef<number>(0);

  useEffect(() => {
    // Update ref to current state value
    currentPercentRef.current = animatedPercent;

    // Determine target percentage
    let targetPercent = 0;

    if (isComplete) {
      targetPercent = 100;
    } else if (!isRunning) {
      targetPercent = 0;
    } else if (actualProgress !== undefined) {
      // Use actual progress but cap at 99% unless complete
      targetPercent = Math.max(1, Math.min(99, actualProgress * 100));
    } else {
      // Fallback to estimated progress for running state
      targetPercent = 70;
    }

    // Cancel any existing animation
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    // Check if this is a new target (either starting fresh or target changed significantly)
    const targetChange = Math.abs(targetPercent - lastTargetRef.current);
    const currentToLastTarget = Math.abs(
      currentPercentRef.current - lastTargetRef.current,
    );

    // New target if:
    // 1. Target changed by more than 5%, OR
    // 2. We're close to the previous target (within 3%) and new target is significantly different (>10%)
    const isNewTarget =
      targetChange > 5 || (currentToLastTarget < 3 && targetChange > 10);

    if (isNewTarget) {
      animationStartTimeRef.current = Date.now();
      lastTargetRef.current = targetPercent;
    }

    // Smooth animation to target with startup easing
    const animate = () => {
      const currentPercent = currentPercentRef.current;
      const distance = Math.abs(targetPercent - currentPercent);

      // If we're very close, just set to target and stop
      if (distance < 0.1) {
        currentPercentRef.current = targetPercent;
        setAnimatedPercent(targetPercent);
        return;
      }

      // Calculate time-based easing for startup smoothness
      const timeElapsed = Date.now() - animationStartTimeRef.current;
      const startupDuration = 500; // 500ms for startup easing
      const startupProgress = Math.min(timeElapsed / startupDuration, 1);

      // Easing function: ease-out-cubic for smooth startup
      const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);
      const startupEase = easeOutCubic(startupProgress);

      // Dynamic speed based on distance - easing function
      const distanceProgress = Math.min(distance / 50, 1);
      const baseSpeed = 0.08 + distanceProgress * 0.02;

      // Apply startup easing multiplier (starts slow, builds up)
      const startupMultiplier = 0.3 + startupEase * 0.7; // 30% to 100% speed
      const finalSpeed = baseSpeed * startupMultiplier;

      // Determine direction and calculate step
      const step = distance * finalSpeed;
      const direction = targetPercent > currentPercent ? 1 : -1;

      // Update current value
      let newPercent = currentPercent + step * direction;

      // Don't overshoot
      if (direction > 0 && newPercent > targetPercent) {
        newPercent = targetPercent;
      } else if (direction < 0 && newPercent < targetPercent) {
        newPercent = targetPercent;
      }

      // Update refs and state
      currentPercentRef.current = newPercent;
      setAnimatedPercent(newPercent);

      // Continue animation if not at target
      if (Math.abs(targetPercent - newPercent) > 0.1) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    // Start the animation
    animationRef.current = requestAnimationFrame(animate);

    // Cleanup on unmount or dependency change
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isRunning, isComplete, actualProgress, animatedPercent]);

  const getStatus = () => {
    if (isRunning) return 'active';
    if (isComplete) return 'success';
    return 'normal';
  };

  return (
    <ProgressContainer>
      <Progress
        type="circle"
        percent={Math.round(animatedPercent)}
        status={getStatus()}
        size={size}
        strokeColor={theme.colorPrimary}
      />
    </ProgressContainer>
  );
};
